import { FaCodeBranch } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import type { TFunction } from "i18next";
import Spinner from "../../../components/Spinner";
import type { GiteaSummary, GiteaWorkflowStatusKey } from "../types/dashboard.types";
import { healthBadgeClass } from "../utils/dashboardStatus.utils";

interface GiteaActionTileProps {
  loading: boolean;
  error: string | null;
  summary: GiteaSummary;
  total: number;
  onClick: () => void;
  t: TFunction;
}

const statusOrder: GiteaWorkflowStatusKey[] = ["new", "inProgress", "onHold", "rejected", "closed", "open", "unknown"];

const statusLabel = (key: GiteaWorkflowStatusKey, t: TFunction): string => {
  const labels: Record<GiteaWorkflowStatusKey, string> = {
    new: t("dashboard.gitea.workflow.new", { defaultValue: "New" }),
    inProgress: t("dashboard.gitea.workflow.in_progress", { defaultValue: "In progress" }),
    onHold: t("dashboard.gitea.workflow.on_hold", { defaultValue: "On hold" }),
    closed: t("dashboard.gitea.workflow.closed", { defaultValue: "Closed" }),
    rejected: t("dashboard.gitea.workflow.rejected", { defaultValue: "Rejected" }),
    open: t("dashboard.gitea.workflow.open", { defaultValue: "Open" }),
    unknown: t("common.unknown", { defaultValue: "Unknown" }),
  };
  return labels[key];
};

const GiteaActionTile = ({ loading, error, summary, total, onClick, t }: GiteaActionTileProps) => {
  const entries = Object.entries(summary.counts)
    .filter(([key]) => !["rejected", "closed"].includes(key))
    .sort(([a], [b]) => statusOrder.indexOf(a as GiteaWorkflowStatusKey) - statusOrder.indexOf(b as GiteaWorkflowStatusKey));
  const status = loading ? "warning" : summary.configured === false ? "disabled" : error ? "error" : "online";
  const title =
    status === "warning"
      ? t("dashboard.gitea.status.checking", { defaultValue: "Checking Gitea..." })
      : status === "disabled"
        ? t("dashboard.gitea.status.not_configured", { defaultValue: "Gitea is not configured" })
        : status === "error"
          ? t("dashboard.gitea.status.error", { defaultValue: "Gitea error" })
          : t("dashboard.gitea.status.ok", { defaultValue: "Gitea is working" });

  return (
    <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_12px_30px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/60">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch xl:justify-between">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <FaCodeBranch className="text-2xl text-emerald-600" />
            <span>{t("dashboard.gitea.label", { defaultValue: "Gitea issues" })}</span>
          </p>
          {loading && !summary.total ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
              <Spinner />
              {t("common.loading", { defaultValue: "Loading..." })}
            </div>
          ) : error ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>
          ) : !summary.configured ? (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{t("dashboard.gitea.not_configured", { defaultValue: "Gitea is not configured." })}</p>
          ) : (
            <>
              <p className="mt-2 text-3xl font-semibold text-slate-900 dark:text-white">{total}</p>
              <div className="mt-3 space-y-1 text-sm text-slate-700 dark:text-slate-200">
                {entries.map(([key, count]) => (
                  <div key={key} className="flex min-w-0 items-center justify-between gap-3">
                    <span className="min-w-0 flex-1 truncate">{statusLabel(key as GiteaWorkflowStatusKey, t)}</span>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex min-w-0 flex-col items-end justify-between gap-3 xl:min-w-[140px]">
          <div className="ml-auto w-full min-w-0 max-w-full text-right text-xs font-semibold text-slate-500 opacity-0 select-none dark:text-slate-300">
            <FiClock className="inline" /> 00:00
          </div>
          <button
            type="button"
            onClick={onClick}
            className="inline-flex max-w-full shrink items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:-translate-y-0.5 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 motion-reduce:transform-none self-stretch xl:self-auto"
            disabled={loading}
          >
            <span className="truncate">{t("common.open", { defaultValue: "Open" })}</span>
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold text-white">{total}</span>
          </button>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${healthBadgeClass(status)}`} title={title} aria-label={title}>
            {status === "online"
              ? t("status.online", { defaultValue: "Online" })
              : status === "warning"
                ? t("status.warning", { defaultValue: "Warning" })
                : status === "disabled"
                  ? t("status.disabled", { defaultValue: "Disabled" })
                  : t("status.error", { defaultValue: "Error" })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GiteaActionTile;
